<template>
  <div class="custom-list-items">
    <div class="setting-header v-center space-between">
      <div class="setting-heading-box">
          <img class="btn-icon setting-icon" src="~@/assets/images/icons/customlist-icon.svg" alt="" width="15px" />
        <h4 class="setting-name">
          Custom List
        </h4>
        <p class="setting-subscript">Manage your custom list categories</p>
    </div>
     <img
        v-if="isTenantAdmin"
        v-tooltip="'Add New List'"
        src="~@/assets/images/icons/add-new-icon.svg"
        alt=""
        height="20px"
        @click="addNewCustomList"
      />
    </div>
    <div class="custom-list-items-container" v-click-outside="cancelEdit">
      <div v-if="addNew" class="input-group v-center my-1 gap-1 w-100 px-1">
        <input
          type="text"
          class="flex-grow"
          v-model="listName"
          placeholder="Enter new customlist value"
        />
        <div class="v-center cus-gap">
          <img
            @click="save"
            class="save"
            src="~@/assets/images/icons/save-icon.svg"
            alt=""
            width="18"
            v-tooltip="Save"
          />
          <img
            @click="close"
            class="close"
            src="~@/assets/images/close.png"
            alt=""
            width="18"
            v-tooltip="Close"
          />
        </div>
      </div>
     <div class="custom-list-search-section">
        <div class="input-group search custom-list-search">
          <input type="text" :value="searchQuery" @input="handleSearchQuery" placeholder="Search lists..." class="search-input" />
        </div>
        <button ref="sortBtn" class="sort-icon-wrapper" type="button" @click.stop="toggleDropdown">
          <img src="~@/assets/images/icons/arrow-up-down.svg" alt="" width="18" />
        </button>
       <transition name="sort-menu-fade">
          <SortDropdown v-if="showSortDropdown" title="Sort by" :primaryOptions="sortOptions"
            :secondaryOptions="sortDirectionOptions" :selectedPrimary="selectedPrimary"
            :selectedSecondary="selectedSecondary" :dropdownStyle="dropdownStyle" @select-primary="handlePrimary"
            @select-secondary="handleSecondary" @close="showSortDropdown = false; removeDropdownListeners()" />
        </transition>
      </div>
      <div
        v-for="item in list"
        :key="item.id"
        class="custom-list-items-item v-center"
        :class="{ selected: selected?.id === item.id }"
      >
        <div class="v-center w-100" @click="select(item, index)">
          <div
            class="input-group v-center w-100 gap-1"
            v-if="!item.system_generated === true && selectToEdit === item.id"
          >
            <input v-model="editName" type="text" class="flex-grow" />
            <div
              class="col-action flex cus-gap"
              v-if="selectToEdit === item.id"
            >
              <img
                width="18px"
                @click="editCustomList(index)"
                src="~@/assets/images/icons/save-icon.svg"
                alt=""
                class="mr-1"
              />
              <img
                class="mr-1"
                style="width: 18px"
                @click.stop="cancelEdit(item)"
                src="~@/assets/images/close.png"
                alt=""
              />
            </div>
          </div>
          <div class="w-100 customlist-bar" v-else>
            <setting-bar
              :title="item.name"
              :fade="addNew || selectToEdit"
              :selected="selected.id === item.id"
              :count="item.custom_list_values_aggregate.aggregate.count"
            >
              <template #actions>
                <div
                  class="toggle-button center v-center cus-gap"
                  v-if="item.system_generated === false && showEdit"
                >
                  <img
                    @click.stop="[edit(item), close()]"
                    src="~@/assets/images/pencil.svg"
                    width="15"
                    alt=""
                  />
                  <img
                    v-tooltip="tooltipFunc(item)"
                    :class="(item.template_fields.length === 0 && item?.id_generation_rules?.length === 0) ? '' : 'disabled'"
                    @click.stop="deleteItem(item)"
                    src="~@/assets/images/trash-2.svg"
                    width="15px"
                    alt=""
                  />
                </div>
              </template>
            </setting-bar>
          </div>
        </div>
      </div>
      <div v-if="loadingMore" class="center py-2 text-xs color-grey">
        Loading...
      </div>
    </div>
  </div>
</template>

<script>
import {
  validateCustomList,
  CreateCustomList,
  DeleteCustomList,
  UpdateCustomList
} from '@/api'
import { success, alert } from '../../../plugins/notification'
import { mapGetters } from 'vuex'
import ConfirmationDialog from '@/plugins/confirmationDialog'
import Loader from '@/plugins/loader'
import settingBar from '@/components/common/settingsBar'
import SortDropdown from '@/components/common/sortDropdown.vue'
// import { sortList } from '@/utils/array'
import arrowUpIcon from '@/assets/images/icons/arrow-up-icon.svg'
import arrowDownIcon from '@/assets/images/icons/arrow-down-icon.svg'
import { startGlobalCursorLoader, stopGlobalCursorLoader } from '@/utils/globalCursorLoader'

export default {
  name: 'custom-list-items',
  components: { settingBar, SortDropdown },
  props: {
    list: {
      type: Array,
      required: true
    },
    selected: {
      type: Object,
      required: true
    },
    loadingMore: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      addNew: false,
      listName: '',
      creating: false,
      selectToEdit: null,
      editName: '',
      showSortDropdown: false,
      dropdownStyle: {},
      searchQuery: '',
      sortOptions: [
        { label: 'Name', value: 'name' },
        // { label: 'Item Count', value: 'itemCount' },
        { label: 'Created Date', value: 'created_on' },
        { label: 'Modified Date', value: 'updated_on' }
      ],
      sortDirectionOptions: [
        { label: 'Ascending', value: 'asc', icon: arrowUpIcon },
        { label: 'Descending', value: 'desc', icon: arrowDownIcon }
      ],
      selectedPrimary: 'updated_on',
      selectedSecondary: 'desc',
      searchTimeout: null
    }
  },
  computed: {
    ...mapGetters(['user']),
    isTenantAdmin () {
      return this.user.tenantLevelRole === 'ADMIN'
    },
    showEdit () {
      return this.user.tenantLevelRole === 'ADMIN' && !this.selectToEdit
    }
  },
  methods: {
    tooltipFunc (item) {
      if (item.template_fields.length !== 0) {
        return 'Attached to Forms'
      } else if (item?.id_generation_rules?.length !== 0) {
        return 'Attached to Sequence Templates'
      } else {
        return 'Delete'
      }
    },
    addNewCustomList () {
      this.addNew = true
      this.$emit('select', { id: null, index: null })
    },
    edit (item) {
      this.selectToEdit = item.id
      this.editName = item.name
    },
    resetSort () {
      this.selectedPrimary = 'updated_on'
      this.selectedSecondary = 'desc'
      this.filteredList()
    },
    cancelEdit () {
      this.selectToEdit = null
      this.editName = ''
    },
    deleteItem (item) {
      if (item.template_fields.length === 0 && item?.id_generation_rules?.length === 0) {
        ConfirmationDialog(
          'Are you sure you want to delete this custom list?',
          (res) => {
            if (res) {
              startGlobalCursorLoader()
              DeleteCustomList(item.id).then((res) => {
                success('Custom List Deleted Successfully')
                this.$emit('update')
              }).catch((err) => {
                this.$notify.alert(err?.message ?? 'Something went wrong')
              }).finally(() => {
                stopGlobalCursorLoader()
              })
            }
          }
        )
      } else alert('Cannot delete the list due to attachment in forms or sequence templates.')
    },
    close () {
      this.addNew = false
      this.listName = ''
    },
    async save () {
      if (!this.listName) return
      const customName = `%${this.listName}%`
      this.creating = true
      const loader = new Loader()
      loader.show()
      const res = await validateCustomList(customName)
      if (!res.core_custom_list.length) {
        CreateCustomList([], this.listName)
          .then((res) => {
            success('Custom list created successfully')
            this.$emit('update', res.data)
            this.creating = false
            this.addNew = false
            this.listName = ''
          })
          .catch((err) => {
            if (err?.message?.includes('Uniqueness violation')) {
              this.$notify.alert('Custom list value already exists')
            } else {
              this.$notify.alert(err?.message ?? 'Something went wrong')
            }
          })
          .finally(() => {
            loader.hide()
          })
      } else {
        const id = res.core_custom_list[0].id
        UpdateCustomList(id, {
          deleted: false,
          name: this.listName
        })
          .then((res) => {
            this.$emit('update', res.data)
            success('Custom list Created successfully')
          })
          .catch((err) => {
            if (err?.message?.includes('Uniqueness violation')) {
              this.$notify.alert('Custom list value already exists')
            } else {
              this.$notify.alert(err?.message ?? 'Something went wrong')
            }
          })
          .finally(() => {
            loader.hide()
          })
      }
    },
    editCustomList (index) {
      UpdateCustomList(this.selectToEdit, {
        name: this.editName,
        deleted: false
      })
        .then((res) => {
          this.$emit('update', { id: this.selected.id, index })
          success('Custom list edited successfully')
          this.selectToEdit = null
          this.editName = ''
        })
        .catch((err) => {
          if (err?.message?.includes('Uniqueness violation')) {
            this.$notify.alert('This Value cant renamed, "Add New List"')
          } else {
            this.$notify.alert(err?.message ?? 'Something went wrong')
          }
        })
    },
    select (item, index) {
      if (this.addNew) {
        this.close()
      }
      if (!this.selectToEdit) {
        const index = this.list.findIndex(listItem => String(listItem.id) === String(item.id))
        this.$emit('select', { id: item.id, index })
      }
    },
    onScroll (e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        if (!this.loadingMore) {
          this.$emit('load-more')
        }
      }
    },
    handlePrimary (option) {
      this.selectedPrimary = option.value
      this.filteredList()
    },
    handleSecondary (option) {
      this.selectedSecondary = option.value
      this.filteredList()
    },
    toggleDropdown () {
      this.showSortDropdown = !this.showSortDropdown
      if (this.showSortDropdown) {
        this.updateDropdownPosition()
        this.$nextTick(() => {
          this.addDropdownListeners()
        })
      } else {
        this.removeDropdownListeners()
      }
    },
    updateDropdownPosition () {
      const btn = this.$refs.sortBtn
      if (!btn || !this.showSortDropdown) return

      const rect = btn.getBoundingClientRect()
      const dropdownHeight = 200
      const dropdownWidth = 245

      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top

      const top = (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight)
        ? rect.top - dropdownHeight : rect.bottom + 4

      let left = rect.left
      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 8
      }

      if (left < 8) left = 8

      this.dropdownStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 9999
      }
    },
    addDropdownListeners () {
      window.addEventListener('resize', this.updateDropdownPosition)
      window.addEventListener('scroll', this.updateDropdownPosition, true)

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.updateDropdownPosition)
        window.visualViewport.addEventListener('scroll', this.updateDropdownPosition)
      }
    },
    removeDropdownListeners () {
      window.removeEventListener('resize', this.updateDropdownPosition)
      window.removeEventListener('scroll', this.updateDropdownPosition, true)

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.updateDropdownPosition)
        window.visualViewport.removeEventListener('scroll', this.updateDropdownPosition)
      }
    },
    handleSearchQuery (event) {
      if (this.searchTimeout) { clearTimeout(this.searchTimeout) }

      this.searchQuery = event.target.value
      this.searchTimeout = setTimeout(() => {
        this.filteredList()
      }, 300)
    },
    filteredList () {
      const orderBy = {}
      orderBy[this.selectedPrimary] = this.selectedSecondary
      let where = {}
      if (this.searchQuery.trim()) {
        where = { name: { _ilike: `%${this.searchQuery.trim()}%` } }
      }
      this.$emit('fetch-data', {
        where,
        orderBy: [orderBy]
      })
    }
  },
  watch: {
    // Watchers removed to prevent infinite loops
  },
  beforeDestroy () {
    this.$el.removeEventListener('scroll', this.onScroll)
    this.removeDropdownListeners()
  },
  mounted () {
    this.$el.addEventListener('scroll', this.onScroll)
  }
}
</script>

<style lang="scss" scoped>
.custom-list-items {
  height: 100%;
  padding: 0px 0px 6px 0px;
  overflow: auto;

  .custom-list-items-container {
      input {
        font-size: var(--text-s);
        color: rgb(83, 83, 83);
      }
    }

  &--add-new {
    position: relative;
    margin-top: 6px;
    .col-action {
      width: 60px;
      img {
        width: 20px;
        cursor: pointer;
      }
    }
    input {
      width: 100%;
      height: 30px;
      border: 1px solid var(--brand-color);
      background: transparent;
      padding-right: 50px;
      font-size: var(--text-base);
    }
    img {
      position: absolute;
      cursor: pointer;
      &.close {
        width: 18px;
        right: 2px;
      }
      &.save {
        width: 20px;
        right: 25px;
      }
    }
  }
  &--bar {
    margin: -6px;
    margin-bottom: 0px;
    padding: 10px;
    background-color: var(--brand-color);
    h4 {
      font-size: var(--text-base);
      font-weight: 500;
    }
    img {
      width: 20px;
      height: 20px;
      cursor: pointer;
    }
  }
  .disabled {
    //background-color: #d3d3d3; /* Light grey background */
    color: #7a7a7a; /* Dark grey text */
    opacity: 0.4;
    //pointer-events: none;
  }
  .cus-gap {
    gap: 4px;
  }
  .setting-header {
    position: relative;
    z-index: 4;
    background: #fff;
    padding: 8px 10px;
  }
  .custom-list-search-section {
     position: relative;
     z-index: 3;
     background: #fff;
     padding: 3px 8px;
     border-bottom: 1px solid #f0f0f0;
     display: flex;
     align-items: center;
     gap: 8px;

    .search-input {
      border: none;
      padding: 6px 10px;
      padding-left: 30px;
      border-radius: 5px;
      font-size: var(--text-s);
      color: rgb(108, 108, 108);
    }

    .custom-list-search {
      flex: 1;
      min-width: 0;
    }

    .sort-icon-wrapper {
      flex-shrink: 0;
      &:hover {
       background-color: var(--brand-color);
      }
    }

    &::after {
      left: 0.45em;
      right: auto;
    }
  }
  .toggle-button {
    display: none;
  }
  .customlist-bar:hover .toggle-button {
    display: flex;
  }

  .custom-list-search {
  &::after {
    left: 5px;
    right: auto;
    top: 50%;
    transform: translateY(-50%);
    filter: grayscale(100%) opacity(60%);
  }

  input {
    padding-left: 34px;
    padding-right: 10px;
  }
}
.sort-icon-wrapper {
  width: 28px;
  height: 28px;
  border: 1px solid #e5e7eb;
  border-radius: 3px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: background-color 220ms ease, border-color 220ms ease, color 220ms ease;

  img {
    width: 16px;
    height: 16px;
    color: #eaedf1;
  }
}
}
</style>
